/** Tiny dependency-free SVG line chart. Renders a filled area under a smooth line. */
interface SparklineProps {
  data: number[];
  height?: number;
  className?: string;
}

export function Sparkline({ data, height = 140, className = "" }: SparklineProps) {
  const width = 800; // rendered via viewBox, scales to container
  if (data.length === 0) return null;
  const max = Math.max(1, ...data);
  const stepX = width / Math.max(1, data.length - 1);
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - (v / max) * (height - 16) - 8;
    return { x, y };
  });

  // Build a smooth path with catmull-rom-ish Bezier control points.
  const linePath = points
    .map((p, i, arr) => {
      if (i === 0) return `M${p.x},${p.y}`;
      const prev = arr[i - 1];
      const cx = (prev.x + p.x) / 2;
      return `C${cx},${prev.y} ${cx},${p.y} ${p.x},${p.y}`;
    })
    .join(" ");

  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={`w-full block ${className}`}
      role="img"
    >
      <defs>
        <linearGradient id="sparkfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#sparkfill)" className="text-accent-500" />
      <path d={linePath} fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-400" />
    </svg>
  );
}
